import { useEffect, useState } from "react";

import useLocalization from "../../hooks/useLocalization";

import { Box } from "@mui/material";

import Input from "../elements/Input";
import EditorToolbar from "./EditorToolbar";
import Switch from "../elements/Switch";
import Select from "../elements/Select";
import Date from "../elements/Date";
import Availability from "../ui/Availability";

const surveySchema = {
  _id: null,
  name: null,
  name_fr: null,
  isActive: true,
  isMandatory: true,
  allowSaveIfIncomplete: false,
  surveyType: "survey",
};

const normalizeMandatory = (value, fallback = true) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "1" || normalized === "true") return true;
    if (normalized === "0" || normalized === "false") return false;
  }
  return fallback;
};

const SurveyEditor = ({
  data,
  surveyId,
  surveys,
  onSave,
  onDelete,
  message,
}) => {
  const { getLabel } = useLocalization();

  const [survey, setSurvey] = useState({ ...surveySchema });

  useEffect(() => {
    if (data) {
      setSurvey({
        ...data,
        surveyType: data.surveyType ? data.surveyType : "survey",
        isMandatory: normalizeMandatory(data.isMandatory, true),
      });
      return;
    }

    if (!surveyId) return;

    const selectedSurvey = surveys.find((t) => t.id === surveyId);
    setSurvey({
      ...selectedSurvey,
      isMandatory: normalizeMandatory(selectedSurvey?.isMandatory, true),
    });
  }, [surveyId]);

  const handleChange = (field, newValue) => {
    setSurvey((current) => {
      if (field === "onlyListedSkus") {
        let orderConfig;
        if (current.orderConfig) {
          orderConfig = { ...current.orderConfig };
        } else {
          orderConfig = {};
        }

        orderConfig.onlyListedSkus = newValue;
        return { ...current, orderConfig: orderConfig };
      } else {
        return { ...current, [field]: newValue };
      }
    });
  };

  const handleSave = () => {
    onSave(survey);
  };

  const handleDelete = () => {
    onDelete(survey);
  };

  return (
    <Box width="100%">
      <EditorToolbar
        onSave={handleSave}
        onDelete={surveyId ? handleDelete : null}
      />

      <Box
        display="flex"
        flexDirection="column"
        gap={1}
        padding="10px"
        height="325px"
        overflow="auto"
      >
        <Input
          id="name"
          label={getLabel("name")}
          value={survey?.name || ""}
          onChange={(newValue) => handleChange("name", newValue)}
          width="100%"
        />
        <Input
          id="name_fr"
          label={getLabel("name_fr")}
          value={survey?.name_fr || ""}
          onChange={(newValue) => handleChange("name_fr", newValue)}
          width="100%"
        />

        <Select
          label={getLabel("surveyType")}
          value={survey && survey.surveyType ? survey.surveyType : "survey"}
          onChange={(newValue) => handleChange("surveyType", newValue)}
          items={[
            { id: "survey", name: "Survey" },
            { id: "order", name: "Order" },
            { id: "training", name: "Training" },
          ]}
        />

        {survey.surveyType === "order" && (
          <Box>
            <Switch
              label={getLabel("onlyListedSkus")}
              value={survey ? survey?.orderConfig?.onlyListedSkus : true}
              onChange={(newValue) => handleChange("onlyListedSkus", newValue)}
            />
          </Box>
        )}

        <Switch
          label={getLabel("isMandatory")}
          value={normalizeMandatory(survey?.isMandatory, true)}
          onChange={(newValue) => handleChange("isMandatory", newValue)}
        />

        <Switch
          label={getLabel("isActive")}
          value={survey ? survey.isActive : true}
          onChange={(newValue) => handleChange("isActive", newValue)}
        />
      </Box>
    </Box>
  );
};

export default SurveyEditor;
